/**
 * Hazır Siparişler — POS garson "Hazır" sekmesi.
 *
 * Mutfaktan READY işaretlenen tüm KDS ticket'ları listeler. Garson
 * "Servis Edildi" butonuyla siparişi alıp masaya götürdüğünü kayda geçer.
 * "Masalarım" filtresi varsayılan açık — kendi siparişleri önce görünür,
 * "Tümü" sekmesi tüm hazır siparişleri gösterir (gel-al/paket dahil).
 */
import { useEffect, useMemo, useState } from "react";
import {
  Stack, Card, Group, Button, Text, Badge, Divider, Loader, Center,
  SegmentedControl,
} from "@mantine/core";
import { IconCheck, IconUser, IconBell } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader, EmptyState } from "../../../shared/ui";
import {
  useReadyKdsTickets,
  useMarkTicketServed,
} from "../../../shared/api";
import { useWebSocket } from "../../../shared/ws";
import { useAuth } from "../../../shared/auth/AuthProvider";

function formatElapsed(seconds: number): string {
  if (!seconds || seconds < 0) return "—";
  if (seconds < 60) return `${seconds} sn`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins} dk`;
  return `${Math.floor(mins / 60)} sa ${mins % 60} dk`;
}

export function ReadyTicketsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { lastMessage, status: wsStatus } = useWebSocket<any>("/ws/waiters/");
  const { data, isLoading } = useReadyKdsTickets({
    wsConnected: wsStatus === "OPEN",
  });
  const markServed = useMarkTicketServed();

  const [scope, setScope] = useState<"mine" | "all">("mine");

  // WS push — yeni hazır geldiğinde / servis edildiğinde listeyi tazele.
  // ÖNEMLİ: useEffect olmalı; useMemo render sonrası invalidate çağırmıyordu
  // → SERVED olan ticket listeden düşmüyor, garson 2. kez tıklayınca
  // "Invalid ticket state transition: SERVED → SERVED" hatası alıyordu.
  useEffect(() => {
    if (!lastMessage) return;
    const ev = lastMessage.event;
    if (ev === "kds.ticket.ready" || ev === "kds.ticket.served") {
      qc.invalidateQueries({ queryKey: ["kds-tickets"] });
    }
  }, [lastMessage, qc]);

  const tickets: any[] = data?.results || data || [];
  const myUserId = user?.id ?? null;

  const isMine = (t: any): boolean =>
    !!myUserId && Number(t.waiter_id) === Number(myUserId);

  const mineTickets = tickets.filter(isMine);
  const visible = scope === "mine" ? mineTickets : tickets;

  // Aynı masanın hazır biletlerini grupla — garson masanın tüm tabaklarını
  // tek seferde "Tümünü Servis Et" ile alabilsin. Biletler fiziksel
  // birleştirilmez; gruplama görsel + toplu eylem içindir. Anahtar table_id
  // (yoksa table_name); ekleme sırası korunur.
  const tableGroups = useMemo(() => {
    const map = new Map<string, { key: string; tableName: string; items: any[] }>();
    for (const t of visible) {
      const key = String(t.table_id ?? t.table_name ?? t.id);
      if (!map.has(key)) {
        map.set(key, { key, tableName: t.table_name || "Masa", items: [] });
      }
      map.get(key)!.items.push(t);
    }
    return Array.from(map.values());
  }, [visible]);

  if (isLoading) {
    return (
      <Center h={300}>
        <Loader />
      </Center>
    );
  }

  async function handleServed(id: string, notify = true) {
    try {
      await markServed.mutateAsync(id);
      if (notify) {
        notifications.show({
          message: "Servis edildi olarak işaretlendi",
          color: "green",
        });
      }
      return true;
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail || "İşlem başarısız",
        color: "red",
      });
      return false;
    }
  }

  /** Bir masadaki tüm hazır biletleri sırayla servis edildi işaretle. Her
   *  bilet bağımsız markServed çağrısıdır; hata olursa o biletten sonrası
   *  durmaz (servis fiziksel iş — birini kaçırmamak için hepsini dener),
   *  sonda tek özet gösterilir. */
  async function handleServeAll(items: any[]) {
    let ok = 0;
    for (const t of items) {
      const success = await handleServed(t.id, false);
      if (success) ok++;
    }
    if (ok > 0) {
      notifications.show({
        message:
          ok === items.length
            ? `${ok} sipariş servis edildi`
            : `${ok}/${items.length} sipariş servis edildi — kalanı kontrol edin`,
        color: ok === items.length ? "green" : "orange",
      });
    }
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Hazır Siparişler"
        subtitle={`${visible.length} sipariş mutfakta hazır bekliyor`}
      />

      <SegmentedControl
        fullWidth
        value={scope}
        onChange={(v) => setScope(v as "mine" | "all")}
        data={[
          { label: `Masalarım (${mineTickets.length})`, value: "mine" },
          { label: `Tümü (${tickets.length})`, value: "all" },
        ]}
      />

      {visible.length === 0 ? (
        <EmptyState
          icon={<IconBell size={40} />}
          title={
            scope === "mine"
              ? "Sizin masalarınızda hazır sipariş yok"
              : "Hazır sipariş yok"
          }
          description={
            scope === "mine" && tickets.length > 0
              ? `${tickets.length - mineTickets.length} sipariş başka garsonların masalarında — "Tümü" sekmesinden bakabilirsiniz.`
              : "Mutfak bir siparişi hazır işaretlediğinde burada görünür."
          }
        />
      ) : (
        tableGroups.map((g) => {
          const head = g.items[0];
          const mine = isMine(head);
          const accent = mine ? "green" : head.waiter_id ? "orange" : "blue";
          const multi = g.items.length > 1;

          // Sahiplik rozeti (tekil + grup başlığı ortak).
          const ownershipBadge = mine ? (
            <Badge size="sm" color="green" variant="filled" leftSection={<IconUser size={12} />}>
              Sizin
            </Badge>
          ) : head.waiter_id ? (
            <Badge size="sm" color="orange" variant="light" leftSection={<IconUser size={12} />}>
              {head.waiter_name || "Başka garson"}
            </Badge>
          ) : (
            <Badge size="sm" color="blue" variant="light">
              Atanmamış
            </Badge>
          );

          // Tek biletin gövdesi: kalemler + bekleme süresi (+ tek modda
          // istasyon/saat başlığı). Grup modunda başlık dışarıda olduğundan
          // bilet başına kompakt gösterilir.
          const ticketBody = (t: any) => (
            <Stack gap={4}>
              <Group justify="space-between" wrap="nowrap">
                <Badge size="sm" variant="light" color="gray">
                  {t.station_code}
                </Badge>
                <Text size="xs" c="dimmed">
                  Hazır: {formatElapsed(t.elapsed_seconds || 0)} önce
                </Text>
              </Group>
              {(t.lines || []).map((l: any, i: number) => (
                <Group key={i} justify="space-between" wrap="nowrap" gap="xs">
                  <Text size="sm" fw={500} style={{ flex: 1, minWidth: 0 }}>
                    {l.quantity}× {l.product_name}
                  </Text>
                  {Array.isArray(l.modifiers) && l.modifiers.length > 0 && (
                    <Text size="xs" c="dimmed">
                      {l.modifiers.map((m: any) => m.name || m).join(", ")}
                    </Text>
                  )}
                </Group>
              ))}
            </Stack>
          );

          return (
            <Card
              key={g.key}
              padding="md"
              radius="md"
              withBorder
              style={{
                borderColor: `var(--mantine-color-${accent}-5)`,
                borderWidth: mine ? 2 : 1,
              }}
            >
              <Stack gap="xs">
                <Group justify="space-between" wrap="nowrap">
                  <Group gap={6} wrap="nowrap">
                    <Badge size="lg" color={accent}>
                      {g.tableName}
                    </Badge>
                    {ownershipBadge}
                    {multi && (
                      <Badge size="sm" color="blue" variant="light">
                        {g.items.length} sipariş
                      </Badge>
                    )}
                  </Group>
                  {!multi && (
                    <Text size="sm" c="dimmed">
                      {head.ready_at
                        ? new Date(head.ready_at).toLocaleTimeString("tr-TR", {
                            hour: "2-digit", minute: "2-digit",
                          })
                        : "—"}
                    </Text>
                  )}
                </Group>

                {!mine && head.waiter_id && (
                  <Text size="xs" c="orange.7">
                    Bu masa {head.waiter_name || "başka bir garsona"} ait — yine
                    de servis edebilirsiniz.
                  </Text>
                )}

                {multi ? (
                  <>
                    {/* Her hazır bilet ayrı blok — kendi kalemleri + tekil
                        "Servis Et"i. Üstte toplu servis. */}
                    {g.items.map((t: any) => (
                      <Card key={t.id} padding="sm" radius="sm" withBorder
                        bg="var(--mantine-color-gray-0)">
                        <Stack gap={6}>
                          {ticketBody(t)}
                          <Button
                            color="green"
                            variant="light"
                            size="xs"
                            leftSection={<IconCheck size={14} />}
                            onClick={() => handleServed(t.id)}
                            loading={markServed.isPending}
                          >
                            Servis Et
                          </Button>
                        </Stack>
                      </Card>
                    ))}
                    <Button
                      color="green"
                      variant={mine ? "filled" : "light"}
                      leftSection={<IconCheck size={18} />}
                      onClick={() => handleServeAll(g.items)}
                      loading={markServed.isPending}
                      fullWidth
                      size="md"
                      mt="xs"
                    >
                      Tümünü Servis Et ({g.items.length})
                    </Button>
                  </>
                ) : (
                  <>
                    <Divider />
                    {ticketBody(head)}
                    <Button
                      color="green"
                      variant={mine ? "filled" : "light"}
                      leftSection={<IconCheck size={18} />}
                      onClick={() => handleServed(head.id)}
                      loading={markServed.isPending}
                      fullWidth
                      size="md"
                    >
                      Servis Edildi
                    </Button>
                  </>
                )}
              </Stack>
            </Card>
          );
        })
      )}
    </Stack>
  );
}
